import React, { useState, useEffect } from 'react';
import {
  Container,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Box,
  CircularProgress,
  Avatar,
  FormControlLabel,
  Switch,
} from '@material-ui/core';
import { Alert } from '@material-ui/lab';
import { makeStyles } from '@material-ui/core/styles';
import { Camera, Sparkles, Download } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useTrainingJobListener } from '../../hooks/useTrainingJobListener';
import VisualScenarioCarousel from './VisualScenarioCarousel';

const useStyles = makeStyles((theme) => ({
  container: {
    minHeight: '100vh',
    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    padding: theme.spacing(3),
  },
  card: {
    maxWidth: 800,
    margin: '0 auto',
    borderRadius: 16,
    boxShadow: '0 20px 40px rgba(0,0,0,0.1)',
  },
  header: {
    textAlign: 'center',
    marginBottom: theme.spacing(3),
  },
  icon: {
    width: 64,
    height: 64,
    backgroundColor: theme.palette.primary.main,
    margin: '0 auto',
    marginBottom: theme.spacing(2),
  },
  promptInput: {
    marginBottom: theme.spacing(3),
  },
  generateButton: {
    background: 'linear-gradient(45deg, #667eea 30%, #764ba2 90%)',
    color: 'white',
    fontWeight: 600,
    padding: theme.spacing(1.5, 4),
    fontSize: '1.1rem',
    '&:hover': {
      background: 'linear-gradient(45deg, #5a67d8 30%, #6b46c1 90%)',
    },
  },
  resultContainer: {
    marginTop: theme.spacing(3),
    textAlign: 'center',
  },
  generatedImage: {
    maxWidth: '100%',
    borderRadius: 12,
    boxShadow: '0 8px 32px rgba(0,0,0,0.1)',
  },
  downloadButton: {
    marginTop: theme.spacing(2),
    backgroundColor: theme.palette.secondary.main,
    color: 'white',
    '&:hover': {
      backgroundColor: theme.palette.secondary.dark,
    },
  },
  examplePrompts: {
    marginTop: theme.spacing(2),
    padding: theme.spacing(2),
    backgroundColor: theme.palette.grey[50],
    borderRadius: 8,
  },
  exampleChip: {
    margin: theme.spacing(0.5),
    cursor: 'pointer',
    transition: 'all 0.2s',
    '&:hover': {
      backgroundColor: theme.palette.primary.light,
      color: 'white',
    },
  },
}));

const examplePrompts = [
  'professional headshot in a suit',
  'casual outdoor portrait',
  'friendly smile in natural lighting',
  'relaxed pose in cozy indoor setting',
  'confident business professional',
  'warm candid portrait',
];

function Generate() {
  const classes = useStyles();
  const { currentUser, userData } = useAuth();
  const { trainingJob } = useTrainingJobListener(currentUser?.uid);
  const [prompt, setPrompt] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);
  const [generatedImage, setGeneratedImage] = useState(null);
  const [error, setError] = useState(null);
  const useAmateurPhotography = true; // Always enabled for better results
  const [useScenarioCarousel, setUseScenarioCarousel] = useState(true);
  const [triggerWord, setTriggerWord] = useState('USER_xyz');

  // Update trigger word when user data or training job data is available
  useEffect(() => {
    if (userData?.name) {
      // Use only the first name as the trigger word
      const firstName = userData.name.split(' ')[0];
      setTriggerWord(firstName);
    } else if (trainingJob && trainingJob.status === 'completed' && trainingJob.triggerWord) {
      // Fallback to training job trigger word if no name available
      setTriggerWord(trainingJob.triggerWord);
    }
  }, [userData, trainingJob]);

  const handleGenerate = async () => {
    if (!prompt.trim()) {
      setError('Please enter a prompt');
      return;
    }

    setIsGenerating(true);
    setError(null);
    setGeneratedImage(null);

    try {
      // Get Firebase token
      const token = await currentUser.getIdToken();
      
      // Call our generation API
      const response = await fetch('http://localhost:8080/api/generation/generate', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          prompt: prompt.trim(),
          useAmateurPhotography: useAmateurPhotography,
        }),
      });

      const data = await response.json();

      if (!data.success) {
        throw new Error(data.error || 'Failed to generate image');
      }

      setGeneratedImage(data.generation);
      
    } catch (err) {
      console.error('Generation error:', err);
      setError(err.message || 'Failed to generate image. Please try again.');
    } finally {
      setIsGenerating(false);
    }
  };

  const handleExampleClick = (examplePrompt) => {
    setPrompt(examplePrompt);
  };

  const handleScenarioSelect = (scenarioPrompt) => {
    setPrompt(scenarioPrompt);
  };

  const handleSwitchToManual = () => {
    setUseScenarioCarousel(false);
  };

  const handleSwitchToCarousel = () => {
    setUseScenarioCarousel(true);
    setPrompt(''); // Clear manual prompt when switching back
  };

  const handleDownload = async () => {
    if (!generatedImage?.imageUrl) return;
    
    try {
      const response = await fetch(generatedImage.imageUrl);
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `instaworthy-${Date.now()}.webp`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Download error:', err);
      setError('Failed to download image');
    }
  };

  return (
    <Container className={classes.container}>
      <Card className={classes.card}>
        <CardContent>
          <div className={classes.header}>
            <Avatar className={classes.icon}>
              <Sparkles size={32} />
            </Avatar>
            <Typography variant="h4" gutterBottom>
              Generate Photos
            </Typography>
            <Typography variant="body1" color="textSecondary">
              You have 100 pics left. Buy more here.
            </Typography>
          </div>

          {/* Visual Scenario Carousel */}
          {useScenarioCarousel && (
            <VisualScenarioCarousel 
              onScenarioSelect={handleScenarioSelect}
              onSwitchToManual={handleSwitchToManual}
              triggerWord={triggerWord}
            />
          )}

          {/* Editable Prompt Textbox - Always Visible */}
          <TextField
            fullWidth
            variant="outlined"
            label={useScenarioCarousel ? "Your Photo Description" : "Describe the photo you want"}
            placeholder={useScenarioCarousel ? 
              "Select a scenario above to populate this with a detailed prompt, then edit as needed..." :
              "e.g., A full body candid-style photo of you walking around an upscale outdoor venue..."
            }
            value={prompt}
            onChange={(e) => setPrompt(e.target.value)}
            className={classes.promptInput}
            multiline
            rows={useScenarioCarousel ? 10 : 8}
            InputProps={{
              style: { fontSize: '0.875rem' }
            }}
            disabled={isGenerating}
          />

          {/* Switch between modes */}
          {!useScenarioCarousel && (
            <Box display="flex" justifyContent="center" mb={2}>
              <Button
                onClick={handleSwitchToCarousel}
                size="small"
                color="primary"
              >
                ← Back to Visual Scenarios
              </Button>
            </Box>
          )}


          <Box display="flex" justifyContent="center" mb={2}>
            <Button
              variant="contained"
              className={classes.generateButton}
              onClick={handleGenerate}
              disabled={isGenerating || !prompt.trim()}
              startIcon={isGenerating ? <CircularProgress size={20} color="inherit" /> : <Camera />}
            >
              {isGenerating ? 'Generating...' : 'Generate Image'}
            </Button>
          </Box>

          {error && (
            <Alert severity="error" style={{ marginTop: 16 }}>
              {error}
            </Alert>
          )}

          {generatedImage && (
            <div className={classes.resultContainer}>
              <Typography variant="h6" gutterBottom>
                Your Generated Image
              </Typography>
              <img
                src={generatedImage.imageUrl}
                alt="Generated profile"
                className={classes.generatedImage}
              />
              <Box mt={2}>
                <Button
                  variant="contained"
                  className={classes.downloadButton}
                  onClick={handleDownload}
                  startIcon={<Download />}
                >
                  Download Image
                </Button>
              </Box>
         
            </div>
          )}

          {/* Example Prompts - only show when no image is generated and in manual mode */}
          {!generatedImage && !useScenarioCarousel && (
            <div className={classes.examplePrompts}>
              <Typography variant="subtitle2" gutterBottom>
                Try these example prompts:
              </Typography>
              <Box display="flex" flexWrap="wrap" justifyContent="center">
                {examplePrompts.map((example, index) => (
                  <Button
                    key={index}
                    variant="outlined"
                    size="small"
                    className={classes.exampleChip}
                    onClick={() => handleExampleClick(example)}
                    disabled={isGenerating}
                  >
                    {example}
                  </Button>
                ))}
              </Box>
            </div>
          )}
        </CardContent>
      </Card>
    </Container>
  );
}

export default Generate;